// Pure helpers for rendering a connector verdict badge. Kept framework-free so the badge
// vocabulary and reason rules are unit-testable without a DOM. The verdict strings mirror the
// server: Awaiting / Held / Posted / Skipped / Bypassed / Error, or null when the document has
// no connector binding (rendered blank).
//
// This is the single source for the badge config in the shared components package. It mirrors the
// tenant Events-tab copy at serviceops-phuketfit-tenant-plugin/ReservationsApp/Sales/js/Pipeline/
// connectorVerdict.ts and additionally carries the `Bypassed` verdict (a neutral grey badge with
// no reason) used by the per-document payments-modal status column.
//
// Badges render as soft-fill pills (S43): each entry carries the Tailwind classes for the
// day-close pill language (soft fill + inset ring, light + dark variants — the reference style in
// the tenant's QuickBooks/dayCloseStatusStyles.ts). Class strings are whole literals so the
// Tailwind content scanner picks them up — this file is in every consumer's `content` glob.

export interface VerdictBadge {
    // Tailwind colour classes for the pill (fill + text + ring, light and dark). The pill shape
    // classes (rounded-full etc.) live in the shared StatusPill component.
    pillClass: string;
    label: string;
    // De-emphasised verdicts — the explicit empty-state family that describes the *absence* of
    // posting activity rather than a pipeline outcome (rendered in the softer grey/slate pills).
    muted?: boolean;
}

// Pill palettes, mirroring the day-close panel's state palette (tenant dayCloseStatusStyles.ts):
// emerald posted, amber held/pending, sky in-flight, red error/stalled, slate disabled, violet
// legacy close, blue awaiting, grey inert/empty-state.
const PILL_BLUE =
    'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200 ' +
    'dark:bg-blue-400/10 dark:text-blue-300 dark:ring-blue-400/30';
const PILL_AMBER =
    'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200 ' +
    'dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/30';
const PILL_EMERALD =
    'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 ' +
    'dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/30';
const PILL_GRAY =
    'bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-300 ' +
    'dark:bg-gray-400/10 dark:text-gray-300 dark:ring-gray-500/30';
const PILL_RED =
    'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200 ' +
    'dark:bg-red-400/10 dark:text-red-300 dark:ring-red-400/30';
const PILL_SKY =
    'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200 ' +
    'dark:bg-sky-400/10 dark:text-sky-300 dark:ring-sky-400/30';
const PILL_SLATE =
    'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-300 ' +
    'dark:bg-slate-400/10 dark:text-slate-300 dark:ring-slate-500/30';
const PILL_VIOLET =
    'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200 ' +
    'dark:bg-violet-400/10 dark:text-violet-300 dark:ring-violet-400/30';

// Posted/Skipped/Error pills mirror the events-tab statusConfig so the surfaces read
// consistently; Awaiting (blue), Held (amber) and Bypassed (grey) extend it. NotClosed is a
// posting-surface-only verdict (never derived from an event): the row's date has no day-close
// yet, so no journal exists to post — rendered as the muted grey pill.
//
// Posting / Stalled / Disabled / ClosedNotPosted are the rest of the day-close (revenue-row)
// family (S41): a revenue row's status is its date's day-close state, never a pipeline-event
// verdict — so Bypassed can never appear on a revenue row. Colours mirror the day-close panel's
// state palette (sky in-flight, red stalled, slate disabled, violet legacy close).
export const connectorVerdictConfig: Record<string, VerdictBadge> = {
    Awaiting:  { pillClass: PILL_BLUE, label: 'Awaiting' },
    Held:      { pillClass: PILL_AMBER, label: 'Held' },
    Posted:    { pillClass: PILL_EMERALD, label: 'Posted' },
    Skipped:   { pillClass: PILL_GRAY, label: 'Skipped' },
    Bypassed:  { pillClass: PILL_GRAY, label: 'Bypassed' },
    Error:     { pillClass: PILL_RED, label: 'Error' },
    NotClosed: { pillClass: PILL_GRAY, label: 'Not closed', muted: true },
    Posting:         { pillClass: PILL_SKY, label: 'Posting' },
    Stalled:         { pillClass: PILL_RED, label: 'Stalled' },
    Disabled:        { pillClass: PILL_SLATE, label: 'Disabled', muted: true },
    ClosedNotPosted: { pillClass: PILL_VIOLET, label: 'Closed not posted' },
};

// Returns the badge for a verdict, or null when there is no verdict (no connector binding → blank
// column). Unknown verdict strings fall back to a neutral grey pill carrying the raw value.
export function getVerdictBadge(verdict: string | null | undefined): VerdictBadge | null {
    if (!verdict) return null;
    return connectorVerdictConfig[verdict] ?? { pillClass: PILL_GRAY, label: verdict };
}

// A reason is shown only for the verdicts that carry one: Held, Skipped, Error, and Stalled (the
// day-close family's halted-on-error state carries the connector's last error). Awaiting, Posted
// and Bypassed never have a reason, and an empty reason is treated as absent.
export function verdictHasReason(verdict: string | null | undefined, reason: string | null | undefined): boolean {
    return !!reason && (verdict === 'Held' || verdict === 'Skipped' || verdict === 'Error' || verdict === 'Stalled');
}
